import React, { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { ANALYTICS_ROLES, SUPERUSER_ROLES } from "utils/roles";

const PageLoader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "40vh",
      color: "#717680",
      fontSize: 14,
    }}
  >
    Loading...
  </div>
);

const suspense = (node: React.ReactNode) => (
  <Suspense fallback={<PageLoader />}>{node}</Suspense>
);

const Login = lazy(() => import("../pages/Login"));
const Forbidden403 = lazy(() => import("pages/Forbidden"));
const Courses = lazy(() =>
  import("pages/Courses").then((m) => ({ default: m.Courses })),
);
const NetworkErrorPage = lazy(() => import("pages/Error/NetworkErrorPage"));
const NewDesignLive = lazy(() => import("../pages/Live/NewDesignLive"));
const Live = lazy(() => import("../pages/Live"));
const LiveDetail = lazy(() => import("pages/Live/LiveDetail"));
const Users = lazy(() => import("../pages/Users"));
const StudentList = lazy(() => import("../pages/Users/_components/StudentList"));
const EmployeeList = lazy(
  () => import("pages/Users/_components/EmployeeList"),
);
const UserCreate = lazy(() => import("../pages/Users/UserCreate"));
const UserEdit = lazy(() => import("../pages/Users/UserEdit"));
const EmployeeCreate = lazy(() => import("../pages/Users/EmployeeCreate"));
const EmployeeEdit = lazy(() => import("../pages/Users/EmployeeEdit"));
const RolePage = lazy(() => import("../pages/Role/RolePage"));
const Support = lazy(() => import("pages/Support"));
const Schedule = lazy(() => import("../pages/Schedule"));
const Daily = lazy(() => import("../pages/Schedule/Daily"));
const University = lazy(() => import("pages/Buildings"));
const ZdPage = lazy(() => import("pages/Buildings/ZdPage"));
const RoomPage = lazy(() => import("pages/Buildings/RoomPage"));
const UniCamera = lazy(() => import("../pages/Buildings/UniCamera"));
const Cameras = lazy(() => import("../pages/Cameras"));
const Roi = lazy(() => import("pages/ROI/ROICreator"));
const ImageUploads = lazy(() => import("pages/ImageUploads"));
const RoleUsers = lazy(() => import("pages/RoleUsers"));
const AnalyticsStudents = lazy(() => import("pages/AnalyticsStudents"));
const AnalyticsTeachers = lazy(() => import("pages/AnalyticsTeachers"));
const AnalyticsFaculty = lazy(() => import("pages/AnalyticsFaculty"));
const AnalyticsDirections = lazy(() => import("pages/AnalyticsDirections"));
const AnalyticsGroup = lazy(() => import("pages/Group"));
const GroupInfo = lazy(() => import("pages/GroupInfo"));
const FacultyList = lazy(() => import("pages/FacultyList"));
const DirectionsList = lazy(() => import("pages/DirectionsList"));
const SubjectList = lazy(() => import("pages/SubjectsList"));
const TeachersInfoPage = lazy(() => import("pages/TeachersPage"));
const StudentDetailForAdmin = lazy(
  () => import("pages/AnalyticsStudents/StudentDetailForAdmin"),
);
const TeacherDetailForAdmin = lazy(
  () => import("pages/AnalyticsTeachers/TeacherDetailForAdmin"),
);
const NoImagefaculties = lazy(() => import("pages/NoImagefaculties"));
const NoImageStudents = lazy(() => import("pages/NoImageStudents"));
const HemisSyncPage = lazy(() => import("pages/HemisSync"));
const LocationList = lazy(() => import("pages/LocationList"));
const LocationCreate = lazy(() => import("pages/LocationCreate"));
const LocationEdit = lazy(() => import("pages/LocationEdit"));
const LocationRoomConnect = lazy(() => import("pages/LocationRoomConnect"));
const LocationRoomConnectEdit = lazy(
  () => import("pages/LocationRoomConnectEdit"),
);
const LocationsListsCOnnect = lazy(() => import("pages/LocationsListsCOnnect"));
const ExamSchedule = lazy(() => import("pages/ExamSchedule"));
const ExamShcheduleEdit = lazy(() => import("pages/ExamShcheduleEdit"));
const ExamScheduleCreate = lazy(() => import("pages/ExamScheduleCreate"));
const ExamScheduleAnalytics = lazy(() => import("pages/ExamScheduleAnalytics"));
const ExamShcheduleByID = lazy(() => import("pages/ExamShcheduleByID"));
const PracticalAttandance = lazy(
  () => import("pages/PracticalScheduleAttandance"),
);

export const router = createBrowserRouter([
  { path: "/login", element: suspense(<Login />) },
  { path: "/403", element: suspense(<Forbidden403 />) },

  {
    children: [
      { path: "/networkError", element: suspense(<NetworkErrorPage />) },

      {
        element: <ProtectedRoute roles={["super_admin", "admin"]} />,
        children: [
          { path: "/", element: suspense(<Courses />) },
          { path: "/networkError", element: suspense(<NetworkErrorPage />) },

          { path: "/live", element: suspense(<NewDesignLive />) },
          { path: "/new-live", element: suspense(<Live />) },
          {
            path: "/live/:auditorium_id",
            element: suspense(<LiveDetail />),
          },
          {
            path: "/live/:auditorium_id/:camera_id",
            element: suspense(<LiveDetail />),
          },

          { path: "/location/list", element: suspense(<LocationList />) },
          { path: "/location/create", element: suspense(<LocationCreate />) },
          { path: "/location/edit/:id", element: suspense(<LocationEdit />) },
          {
            path: "/location/connect",
            element: suspense(<LocationRoomConnect />),
          },
          {
            path: "/location/connect/list",
            element: suspense(<LocationsListsCOnnect />),
          },
          {
            path: "/practical-schedules",
            element: suspense(<LocationsListsCOnnect />),
          },
          {
            path: "/practical-schedules-attandance",
            element: suspense(<PracticalAttandance />),
          },
          {
            path: "/practical-schedules/edit/:id",
            element: suspense(<LocationRoomConnectEdit />),
          },

          { path: "/exams/", element: suspense(<ExamSchedule />) },
          { path: "/exams/create", element: suspense(<ExamScheduleCreate />) },
          { path: "/exams/:id", element: suspense(<ExamShcheduleByID />) },
          { path: "/exams/edit/:id", element: suspense(<ExamShcheduleEdit />) },
          {
            path: "/exams/analytics",
            element: suspense(<ExamScheduleAnalytics />),
          },

          { path: "/uploads", element: suspense(<ImageUploads />) },

          {
            path: "no-image-faculties",
            element: suspense(<NoImagefaculties />),
          },
          {
            path: "/no-image-students/:department_id",
            element: suspense(<NoImageStudents />),
          },

          {
            element: <ProtectedRoute roles={["super_admin"]} withLayout={false} />,
            children: [
              { path: "/hemis-sync", element: suspense(<HemisSyncPage />) },
            ],
          },

          { path: "/users/add", element: suspense(<UserCreate />) },
          { path: "/users/edit/:id", element: suspense(<UserEdit />) },
          {
            path: "/employees/edit/:id",
            element: suspense(<EmployeeEdit />),
          },
          {
            path: "/users/add-teacher",
            element: suspense(<EmployeeCreate />),
          },
          {
            path: "/users/student/:id/:student_id",
            element: suspense(<StudentDetailForAdmin />),
          },
          {
            path: "/users/employee/:id/:employee_id",
            element: suspense(<TeacherDetailForAdmin />),
          },

          { path: "/support", element: suspense(<Support />) },

          { path: "/university", element: suspense(<University />) },
          {
            path: "/university/buildings/:uni_id",
            element: suspense(<ZdPage />),
          },
          {
            path: "/university/buildings/:uni_id/rooms/:building_id",
            element: suspense(<RoomPage />),
          },
          {
            path: "/university/buildings/:uni_id/rooms/:building_id/camera/:camera_id",
            element: suspense(<UniCamera />),
          },

          { path: "/cameras", element: suspense(<Cameras />) },
          { path: "/roi", element: suspense(<Roi />) },
        ],
      },

      {
        element: (
          <ProtectedRoute
            roles={SUPERUSER_ROLES}
            permissions={["view_roles", "view_user-access"]}
          />
        ),
        children: [
          { path: "/users/add-roles", element: suspense(<RolePage />) },
          { path: "/roleUsers", element: suspense(<RoleUsers />) },
        ],
      },

      {
        element: (
          <ProtectedRoute roles={ANALYTICS_ROLES} permissions={["view_statistics"]} />
        ),
        children: [
          {
            path: "/analytics/students",
            element: suspense(<AnalyticsStudents />),
          },
          {
            path: "/analytics/teachers",
            element: suspense(<AnalyticsTeachers />),
          },
          {
            path: "/analytics/faculty",
            element: suspense(<FacultyList />),
          },
          {
            path: "/analytics/faculty/:id/:faculty_id",
            element: suspense(<AnalyticsFaculty />),
          },
          {
            path: "/analytics/directions",
            element: suspense(<DirectionsList />),
          },
          {
            path: "/analytics/directions/:id",
            element: suspense(<AnalyticsDirections />),
          },
          {
            path: "/analytics/groups",
            element: suspense(<AnalyticsGroup />),
          },
          {
            path: "/analytics/groups/:id",
            element: suspense(<GroupInfo />),
          },
          {
            path: "/analytics/subjects",
            element: suspense(<SubjectList />),
          },
          {
            path: "/analytics/subjects/:id",
            element: suspense(<TeachersInfoPage />),
          },
          {
            path: "/users/",
            element: suspense(<Users />),
            children: [
              {
                path: "/users/student",
                element: suspense(
                  <StudentList pageType="detail" date="" />,
                ),
              },
              {
                path: "/users/employee",
                element: suspense(<EmployeeList pageType="detail" />),
              },
            ],
          },
          { path: "/class-schedule", element: suspense(<Schedule />) },
          {
            path: "/class-schedule/:id/:user_id",
            element: suspense(<Daily />),
          },
        ],
      },
    ],
  },
]);
